import { usePlatewell } from '@platewell/query';
import { useEffect, useRef, useState } from 'react';
import { createShare } from '../lib/api.js';
import { Dialog } from './dialog.js';
import { useShowToast } from './toast.js';

export interface ShareDialogProps {
  title: string;
  kind: 'recipe' | 'collection';
  recipeIds: string[];
  onClose: () => void;
}

/**
 * Sharing hands over a *copy*, not a live link.
 *
 * The recipient's household becomes the owner of what it imports and can edit it freely,
 * while the original stays untouched. That avoids the situation nobody wants — a friend
 * tweaking your grandmother's recipe in your own recipe box — and it means a share link
 * expiring never removes anything from the person who saved it.
 */
export function ShareDialog({ title, kind, recipeIds, onClose }: ShareDialogProps) {
  const { activeHouseholdId } = usePlatewell();
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const showToast = useShowToast();
  // StrictMode runs effects twice in development; without this every open would mint two links.
  const started = useRef(false);

  async function share() {
    if (activeHouseholdId == null) {
      setBusy(false);
      setError('Could not create the link');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await createShare({
        householdId: activeHouseholdId,
        kind,
        recipeIds,
        title,
      });
      setUrl(new URL(result.url, window.location.origin).toString());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create the link');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void share();
    // Created once per open; the dialog's props are fixed for its lifetime.
  }, []);

  async function copy(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      setError(null);
      showToast('Link copied');
    } catch {
      // A browser that refuses the clipboard leaves the link on screen to copy by hand.
      setError('Could not copy the link. Select it above and copy it yourself.');
    }
  }

  return (
    <Dialog onClose={onClose} aria-label={`Share “${title}”`}>
      <h2>Share “{title}”</h2>

      <p className="hint">
        Anyone with the link can view {recipeIds.length}{' '}
        {recipeIds.length === 1 ? 'recipe' : 'recipes'} and save a copy into their own household.
        The link works for 90 days, and your copy stays yours.
      </p>

      <input
        className="input"
        readOnly
        aria-label="Share link"
        value={url ?? ''}
        placeholder={busy ? 'Creating link…' : ''}
        onFocus={(e) => e.target.select()}
      />

      {error != null && <p className="error-text">{error}</p>}

      <div className="row" style={{ marginTop: 16 }}>
        <span className="spacer" />
        {url == null && !busy ? (
          <button className="btn" onClick={() => void share()}>
            Try again
          </button>
        ) : (
          <button
            className="btn"
            disabled={url == null}
            onClick={() => url != null && void copy(url)}
          >
            Copy link
          </button>
        )}
        <button className="btn primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Dialog>
  );
}
